import { deletePlan } from '@commenda/api';
import { Dialog, DialogContent } from '@commenda/kakashi';
import React, { useState } from 'react';
import { useNotification } from '../../hooks/useNotification';
import { PlanCategory } from '../types';
import { CancelPlanStep } from './steps/CancelPlanStep';
import { CancellationReasonStep } from './steps/CancellationReasonStep';
import { CancellationSuccessStep } from './steps/CancellationSuccessStep';
import { PlanDetailsStep } from './steps/PlanDetailsStep';

export type PlanModalStep = 'details' | 'cancel' | 'reason' | 'success';

interface PlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialStep: PlanModalStep;
  name: string;
  category: PlanCategory;
  price: number;
  billingCycle: string;
  nextPaymentDue?: string;
  features?: string[];
  planDetails?: any;
  onCancelPlan?: (reason: string) => void;
  assignedTo?: string;
  entitlementIndex?: number;
  totalEntitlements?: number;
  originalProductId?: string;
  companyId?: number;
  subscriptionId?: string;
  productId?: string;
  onPlanDeleted?: () => void;
}

export const PlanModal = ({
  isOpen,
  onClose,
  initialStep,
  name,
  category,
  price,
  billingCycle,
  nextPaymentDue,
  features,
  planDetails,
  onCancelPlan,
  assignedTo,
  entitlementIndex,
  totalEntitlements,
  originalProductId,
  companyId,
  subscriptionId,
  productId,
  onPlanDeleted,
}: PlanModalProps) => {
  const [currentStep, setCurrentStep] = useState<PlanModalStep>(initialStep);
  const [isLoading, setIsLoading] = useState(false);
  const { setNotification } = useNotification();

  const activeStep = currentStep;

  const handleCancelPlanConfirm = () => {
    setCurrentStep('reason');
  };

  const handleConfirmCancellation = async (reason: string) => {
    if (companyId && subscriptionId && productId) {
      setIsLoading(true);
      try {
        await deletePlan({ companyId, subscriptionId, productId });
        setCurrentStep('success');
        onPlanDeleted?.();
      } catch (error) {
        console.error('Failed to delete plan:', error);
        setNotification({
          show: true,
          valid: false,
          title: 'Unable to Cancel Plan',
          message:
            'We are unable to process this request at the moment. Please try again or contact support.',
        });
      } finally {
        setIsLoading(false);
      }
    } else {
      onCancelPlan?.(reason);
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      setCurrentStep(initialStep);
    }
  }, [isOpen, initialStep]);

  let jsx = null;
  switch (currentStep) {
    case 'details':
      jsx = (
        <PlanDetailsStep
          onClose={onClose}
          name={name}
          category={category}
          price={price}
          billingCycle={billingCycle}
          nextPaymentDue={nextPaymentDue}
          features={planDetails?.features || features || []}
          planDetails={planDetails}
        />
      );
      break;
    case 'cancel':
      jsx = (
        <CancelPlanStep
          onClose={onClose}
          onConfirm={handleCancelPlanConfirm}
          name={name}
          price={price}
          features={planDetails?.features || features || []}
        />
      );
      break;
    case 'reason':
      jsx = (
        <CancellationReasonStep
          onClose={onClose}
          onConfirm={handleConfirmCancellation}
          type="plan"
          isLoading={isLoading}
        />
      );
      break;
    case 'success':
      jsx = (
        <CancellationSuccessStep
          onClose={onClose}
          name={name}
          expiryDate={nextPaymentDue || ''}
          type="plan"
        />
      );
      break;
    default:
      break;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-none w-fit">{jsx}</DialogContent>
    </Dialog>
  );
};

export default PlanModal;
